/**
 * Decoding the wire: how the generated `Network.*.parse` functions turn
 * response JSON into typed values.
 *
 * Every generated object type carries a `MODEL` field table (required
 * fields, scalar types, enums, nested and inline objects, date and id
 * types). A date, id or rational is checked as the JSON its schema declares
 * (a string; integer `n` and `d`) before it is converted, and an inline
 * object (a union branch such as `{ action: "create" }`) has its own field
 * table; only a schema-less value passes as any JSON. Its
 * `parse(raw: Json)` runs that table through `WireDecode.parse`, which builds
 * a new value and throws a `WireDecodeError` naming the type, the path and
 * the reason when the JSON does not match — a malformed response fails at
 * the boundary instead of travelling on as a mistyped value. Its
 * `safeHydrate` is the non-throwing twin (a `Model.Result`).
 *
 * Response and request types declare each nullable key as a required
 * `field: Option<T>` (a response type every optional key too): their `MODEL`
 * hydrates those fields with `WireDecode.nullable`, so a key the server
 * omitted decodes to `null` and the decoded value always has the key. Their
 * generated `make(fields)` builds one in code (a request body, a fixture, an
 * optimistic row) and takes every key: a nullable one is stated, `null`
 * when there is none. An optional request key that cannot be `null` is
 * `field?: T`, hydrated with `WireDecode.omittable`.
 *
 * A patch type (a PATCH body, `EventUpdate`) is the one place a key is
 * `field?: Option<T>`: absent leaves the field unchanged, `null` clears it.
 * Its fields hydrate with `Field.optional`, which keeps the difference.
 */

import type { Option } from "../core/option.ts";
import { type Field, Model } from "@tento-core/model";
import type { Json, JsonObject } from "../core/json.ts";

// The generated wire files import `Option` with the decoder
// (`import { WireDecode, type Option } from …`): the module type they write
// every nullable field with.
export type { Option } from "../core/option.ts";

/**
 * A wire value that does not match its generated type: a required field is
 * missing, a field has the wrong JSON type or enum value, or a date or id
 * does not parse.
 *
 * `path` and `reason` describe the first mismatch (the message lists them
 * all); `issues` carries every one for callers that report them.
 */
export class WireDecodeError extends Error {
  /** The generated wire type being decoded, e.g. `"SheetRowDb"`. */
  readonly type: string;
  /**
   * Where the first mismatch is, as a dotted path from the decoded value
   * (`"rows.0.id"`); `"$"` for the value itself.
   */
  readonly path: string;
  /** What is wrong at `path`, e.g. `"missing required field"`, `"expected string"`. */
  readonly reason: string;
  /** Every mismatch the decode found. */
  readonly issues: readonly Model.Issue[];

  constructor(type: string, issues: readonly Model.Issue[]) {
    const first = issues[0] ?? Model.issue([], "type", "does not match");
    const path = pathOf(first);
    super(
      `${type}: ${issues.map((issue) => `${pathOf(issue)}: ${issue.message}`).join("; ") ||
        `${path}: ${first.message}`}`,
    );
    this.name = "WireDecodeError";
    this.type = type;
    this.path = path;
    this.reason = first.message;
    this.issues = issues;
  }
}

function pathOf(issue: Model.Issue): string {
  return issue.path.length === 0 ? "$" : issue.path.join(".");
}

/**
 * Wire JSON decoded through a generated type's `MODEL` field table: `parse`
 * (a new value or a thrown `WireDecodeError`), `partial` (the cell decoders'
 * tolerant form), `nullable` (a nullable field) and `omittable` (an optional
 * request field that cannot be `null`). A generated `make` builder, and
 * every create verb (`tasks.create`, `events.create`, `rows.add`,
 * `sheets.create`), takes the type itself: every key stated, `null` the one
 * spelling of none.
 */
export namespace WireDecode {
  /**
   * `raw` decoded through `model`: a new value, or a thrown
   * `WireDecodeError` naming `type`.
   */
  export function parse<T>(type: string, model: Model<T>, raw: Json): T {
    const result = model.safeHydrate(raw);
    if (result.isOk) return result.value;
    throw new WireDecodeError(type, result.issues);
  }

  /**
   * What `partial` decoded: the declared fields that decode (`value`), an
   * issue for each declared field that does not (`issues`, with its path),
   * and the keys the model does not declare, as sent (`undeclared` — not
   * validated, and not part of `value`).
   */
  export interface PartialResult<T> {
    readonly value: Partial<T>;
    readonly issues: readonly Model.Issue[];
    readonly undeclared: JsonObject;
  }

  /**
   * The fields of `raw` that `model` declares and that decode, hydrated
   * (dates, ids, nested objects); a declared field that is absent is left
   * out, one that does not decode is left out AND reported in `issues`, and
   * a key the model does not declare is returned apart, in `undeclared`. For
   * the cell decoders, whose input may be a value the SDK wrote itself (an
   * optimistic event carries no `etag`): each decoder then checks the
   * fields it needs and drops the cell without them.
   */
  export function partial<T>(model: Model.Object<T>, raw: JsonObject): PartialResult<T> {
    const out: Record<string, unknown> = {};
    const undeclared: Record<string, Json> = {};
    const issues: Model.Issue[] = [];
    // Keys, not `Object.entries`: no `[name, value]` pair per field of every decoded cell.
    for (const name of Object.keys(raw)) {
      const value: Json = Reflect.get(raw, name);
      const field = model.fields[name];
      if (field === undefined) {
        undeclared[name] = value;
        continue;
      }
      const result = field.safeHydrate(value, [name], true);
      if (result.isOk) out[name] = result.value;
      else issues.push(...result.issues);
    }
    // Every key of `out` is a declared field its own table decoded: the same
    // trust `Model.object` gives its field table.
    return { value: out as Partial<T>, issues, undeclared };
  }

  /**
   * A nullable field of a response or request type: absent and `null` both
   * decode to `null`, and the decoded object always carries the key.
   * Serializing leaves `null` as `null` and drops an `undefined`.
   */
  export function nullable<T>(model: Model<T>): Field<Option<T>> {
    return {
      // "Always written": `Model.object` keeps a required field's key even
      // when the wire omitted it.
      required: true,
      safeHydrate(raw, path, present) {
        if (!present || raw === null || raw === undefined) return Model.ok(null);
        const result = model.safeHydrate(raw);
        if (result.isOk) return result;
        return Model.issues(
          result.issues.map((issue) => ({ ...issue, path: [...path, ...issue.path] })),
        );
      },
      serialize(value) {
        if (value === undefined) return undefined;
        if (value === null) return null;
        return model.serialize(value);
      },
    };
  }

  /**
   * An optional request field that cannot be `null` (`field?: T`): an absent
   * key stays absent, a `null` is refused (the server does not accept one),
   * a present value decodes through `model`.
   */
  export function omittable<T>(model: Model<T>): Field<T | undefined> {
    return {
      required: false,
      safeHydrate(raw, path, present) {
        if (!present || raw === undefined) return Model.ok(undefined);
        if (raw === null) return Model.err(Model.issue(path, "type", "null is not allowed"));
        const result = model.safeHydrate(raw);
        if (result.isOk) return result;
        return Model.issues(
          result.issues.map((issue) => ({ ...issue, path: [...path, ...issue.path] })),
        );
      },
      serialize(value) {
        if (value === undefined || value === null) return undefined;
        return model.serialize(value);
      },
    };
  }
}
